import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { FillPattern } from "../../types/floorplannerTypes";
import { canonicalizeFillPattern } from "../../utils/fillPatterns";
import { getPatternPreviewDataURL } from "../../utils/patternTextures";
import {
  DEFAULT_ROOM_FLOOR_PATTERN,
  ROOM_FLOOR_PATTERN_OPTIONS,
} from "./roomFloorPatterns";

interface RoomFloorPatternPickerProps {
  value?: FillPattern;
  onChange: (pattern: FillPattern | undefined) => void;
}

/**
 * Compact floor-pattern dropdown for the room-picker wizard.
 * Interior wood/parquet options are listed first.
 */
export const RoomFloorPatternPicker: React.FC<RoomFloorPatternPickerProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    maxHeight: 360,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const resolved = canonicalizeFillPattern(value);
  // Treat explicit `undefined` as "None" — do not fall back to the default pattern.
  const currentOption =
    ROOM_FLOOR_PATTERN_OPTIONS.find((opt) => opt.value === resolved) ??
    ROOM_FLOOR_PATTERN_OPTIONS.find(
      (opt) => opt.value === DEFAULT_ROOM_FLOOR_PATTERN,
    ) ??
    ROOM_FLOOR_PATTERN_OPTIONS[0];

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const dropdownWidth = 220;
    const maxDropdownHeight = 360;
    const minDropdownHeight = 140;
    const viewportPadding = 12;
    const triggerGap = 8;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const shouldOpenUp =
      spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow;
    const availableHeight = shouldOpenUp
      ? spaceAbove - triggerGap
      : spaceBelow - triggerGap;
    const maxHeight = Math.max(
      minDropdownHeight,
      Math.min(maxDropdownHeight, availableHeight),
    );
    const top = shouldOpenUp
      ? rect.top - triggerGap - maxHeight
      : rect.bottom + triggerGap;
    const maxLeft = window.innerWidth - viewportPadding - dropdownWidth;

    setDropdownPosition({
      top: Math.max(
        viewportPadding,
        Math.min(top, window.innerHeight - viewportPadding - maxHeight),
      ),
      left: Math.max(
        viewportPadding,
        Math.min(rect.right - dropdownWidth, maxLeft),
      ),
      maxHeight,
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const dropdown = isOpen
    ? createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] w-[220px] overflow-y-auto rounded border border-gray-300 bg-white shadow-lg"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            maxHeight: `${dropdownPosition.maxHeight}px`,
          }}
        >
          {ROOM_FLOOR_PATTERN_OPTIONS.map((option) => (
            <button
              key={option.value ?? "none"}
              type="button"
              className={`flex w-full items-center px-2 py-2 text-left hover:bg-gray-100 ${
                option.value === resolved ? "bg-indigo-50" : ""
              }`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.value ? (
                <img
                  src={getPatternPreviewDataURL(option.value)}
                  alt={option.label}
                  className="mr-2 h-6 w-6 rounded border border-gray-200"
                />
              ) : (
                <div className="mr-2 h-6 w-6 rounded border border-gray-200 bg-white" />
              )}
              <span className="text-sm">{option.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div>
      <div className="mb-1 text-sm font-medium text-gray-800">
        {t("roomPicker.floorPattern")}
      </div>
      <p className="mb-2 text-xs text-gray-500">
        {t("roomPicker.floorPatternHint")}
      </p>
      <button
        ref={buttonRef}
        type="button"
        className="flex w-full items-center justify-between gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-left text-sm"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="flex min-w-0 items-center gap-2">
          {currentOption.value ? (
            <img
              src={getPatternPreviewDataURL(currentOption.value)}
              alt=""
              className="h-5 w-5 shrink-0 rounded border border-gray-200"
            />
          ) : (
            <span className="h-5 w-5 shrink-0 rounded border border-gray-200 bg-white" />
          )}
          <span className="truncate">{currentOption.label}</span>
        </span>
        <svg
          className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {dropdown}
    </div>
  );
};
